import { API_URL } from '../../config/api';

interface PushSubscriptionJSON {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class PushSubscriptionManager {
  private static instance: PushSubscriptionManager;
  private registration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string;
  private initPromise: Promise<void> | null = null;

  private constructor() {
    this.vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

    if (!this.vapidPublicKey) {
      console.error(
        '[PUSH] REACT_APP_VAPID_PUBLIC_KEY is not set. Push notifications will not work. ' +
        'Make sure the variable is defined in your .env file.'
      );
    }
  }

  public static getInstance(): PushSubscriptionManager {
    if (!PushSubscriptionManager.instance) {
      PushSubscriptionManager.instance = new PushSubscriptionManager();
    }
    return PushSubscriptionManager.instance;
  }

  public async initialize(): Promise<void> {
    // Deduplicate concurrent init calls
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    try {
      await this.initPromise;
    } catch (e) {
      // Allow retry on next call
      this.initPromise = null;
      throw e;
    }
    return;
  }

  private async _doInitialize(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[PUSH] Service Worker or PushManager not supported');
      return;
    }

    try {
      const existingRegistration = await navigator.serviceWorker.getRegistration('/');
      if (existingRegistration) {
        this.registration = existingRegistration;
      } else {
        this.registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      }
      await navigator.serviceWorker.ready;
    } catch (error) {
      console.error('[PUSH] Initialization error:', error);
      throw error;
    }
  }

  public async subscribe(userId: string): Promise<PushSubscriptionJSON> {
    if (!this.vapidPublicKey) {
      throw new Error('VAPID public key is not configured (REACT_APP_VAPID_PUBLIC_KEY)');
    }

    // Ensure we're initialized
    if (!this.registration) {
      await this.initialize();
    }

    if (!this.registration) {
      throw new Error('Service Worker registration unavailable');
    }

    // Reuse existing subscription if available
    const existingSubscription = await this.registration.pushManager.getSubscription();
    if (existingSubscription) {
      const subscriptionJSON = existingSubscription.toJSON();
      await this.sendSubscriptionToBackend(userId, subscriptionJSON);
      return subscriptionJSON as PushSubscriptionJSON;
    }

    // Ensure SW is active before subscribing
    if (!this.registration.active) {
      await navigator.serviceWorker.ready;
      this.registration = await navigator.serviceWorker.getRegistration('/') || this.registration;
    }

    // Wait for controller if needed (max 5s)
    if (!navigator.serviceWorker.controller) {
      await new Promise<void>((resolve) => {
        const onControllerChange = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        setTimeout(resolve, 5000);
      });
    }

    const convertedVapidKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

    if (!this.registration.pushManager) {
      throw new Error('PushManager not available on this registration');
    }

    const subscription = await this.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedVapidKey
    });

    const subscriptionJSON = subscription.toJSON();
    await this.sendSubscriptionToBackend(userId, subscriptionJSON);
    return subscriptionJSON as PushSubscriptionJSON;
  }

  public async unsubscribe(): Promise<void> {
    if (!this.registration) return;

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (error) {
      console.error('[PUSH] Unsubscribe error:', error);
    }
  }

  public isConfigured(): boolean {
    return !!this.vapidPublicKey;
  }

  private async sendSubscriptionToBackend(userId: string, subscription: any): Promise<void> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Auth token not found');
    }

    const response = await fetch(`${API_URL}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId: parseInt(userId, 10),
        subscription
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend error: ${response.status} - ${errorText}`);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default PushSubscriptionManager.getInstance();
